import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useMemo } from 'react';
import flattenDeep from 'lodash/flattenDeep';
import { encodeSelections } from '@/utils/widget/parseSelections';
import { deleteUndefinedValues } from '@/utils/objects';
import useSubscribeWidgetResults from '@/components/widget/visualizations/hooks/useSubscribeWidgetResults';
import { getSelectionGroupItemByIndex } from '@/components/Editor/Form/utils';
import { isCategorical as checkIsCategorical, selectionConfig as bubbleSelectionConfig, useData, useSelections, DEFAULT_COLORS } from '@/components/widget/visualizations/Bubble';
import { DEFAULT_DATA_LABEL_OPTIONS } from '@/components/widget/visualizations/internal/PointChart/utils';
import { overrideConfig } from '@/components/widget/visualizations/internal/PointChart/utils';
import { usePointStyles } from '@/components/widget/visualizations/internal/PointChart/stylesTheme';
import { selectionConfig as scatterSelectionConfig } from '@/components/widget/visualizations/Scatter';
import ColorPalletMenu from '@/components/Editor/Form/fields/ColorPalletMenu';
import Headings from '@/components/Editor/Form/fields/Headings';
import ChartLabels from '@/components/Editor/Form/fields/ChartLabels';
import TrendlineFields from '@/components/Editor/Form/fields/Trendline';
import Collapsible from '@/components/Editor/Form/fields/common/Collapsible';
import ZeroBaselineToggle from '@/components/Editor/Form/fields/common/ZeroBaselineToggle';
import { AxesGridLinesRevealPill, AxisRevealPill } from '@/components/Editor/Form/fields/ChartJsAxes';
import ChartBoundaries from '@/components/Editor/Form/fields/ChartBoundaries';
import Legend from '@/components/Editor/Form/fields/Legend';
import DataLabels from '@/components/Editor/Form/fields/DataLabels';
import Animation from '@/components/Editor/Form/fields/Animation';
import PointStyles, { checkPointStylesModified } from '@/components/Editor/Form/fields/PointStyles';
import Radius from './Radius';
import { defaultProps, DISABLED_POINT_STYLES } from './settings';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const WidgetEditFormPointChartOptions = _ref => {
  var _configWithOverrides$, _configWithOverrides$2, _configWithOverrides$3, _configWithOverrides$4, _configWithOverrides$5, _configWithOverrides$6, _configWithOverrides$7, _globalSeriesOptions$, _globalSeriesOptions$2, _globalSeriesOptions$3, _globalSeriesOptions$4, _globalSeriesOptions$5, _globalSeriesOptions$6, _configWithOverrides$8, _configWithOverrides$9, _configWithOverrides$10, _configWithOverrides$11, _configWithOverrides$12, _configWithOverrides$13;

  let {
    widgetId,
    sectionId,
    reportingWidgetId,
    chartType,
    config,
    hasPointRadius,
    hasPointTemplate,
    pillVariant,
    onChange,
    onEditSelections
  } = _ref;
  const nameSpace = `${widgetId}-${sectionId}`;
  const {
    themeStyles,
    defaultStyles,
    themeCtx: {
      colorPallet: defaultColorPallet = DEFAULT_COLORS
    }
  } = usePointStyles(config);
  const configWithOverrides = useMemo(() => overrideConfig(config), [config]);
  const seriesOptions = configWithOverrides.seriesOptions || [];
  const collectionOptions = configWithOverrides.collectionOptions || [];
  const groupOptions = configWithOverrides.groupOptions || [];
  const {
    selections,
    labelsSelections = '',
    selectionsLabeled = true,
    selectionsStrategy = 'default',
    globalSeriesOptions,
    colorPallet = defaultColorPallet
  } = configWithOverrides; // Get selection config based on chart type

  const selectionConfig = chartType === 'bubble' ? bubbleSelectionConfig : scatterSelectionConfig; // Get current selection results

  const results = useSubscribeWidgetResults(reportingWidgetId); // Apply selections to results

  const {
    selectionResults
  } = useSelections({
    results,
    selections: selections || '',
    selectionsLabeled,
    selectionConfig
  });
  const isCategorical = checkIsCategorical(selectionsStrategy, results); // Combine results with rendering styles

  const {
    dataBoundaries,
    legendsCount
  } = useData({
    selectionResults,
    seriesOptions,
    globalSeriesOptions: globalSeriesOptions || {},
    groupOptions,
    collectionOptions,
    colorPallet,
    isCategorical,
    seriesMinimumLength: selectionConfig.seriesMinimumLength || 2,
    chartType,
    themeStyles
  }); // Check if point styles have been modified

  const isPointStylesModified = useMemo(() => checkPointStylesModified(configWithOverrides.globalSeriesOptions || {}, {
    pointRadius: defaultStyles.pointRadius
  }), [configWithOverrides.globalSeriesOptions, defaultStyles]);
  const exampleValue = useMemo(() => {
    const exampleSelection = getSelectionGroupItemByIndex(flattenDeep(selectionResults), 0);
    return typeof exampleSelection.value !== 'undefined' ? exampleSelection.value : 10;
  }, [selectionResults]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(ColorPalletMenu, {
      label: "Colors",
      colors: colorPallet,
      palletColorOverrides: config.palletColorOverrides,
      openButtonVariant: pillVariant,
      onOverridePalletColors: (name, colors) => onChange('palletColorOverrides', {
        [name]: colors
      }),
      onChange: colorPallet => onChange('', {
        colorPallet
      })
    }), onEditSelections ? /*#__PURE__*/_jsx(ChartLabels, {
      nameSpace: nameSpace,
      selections: labelsSelections ? encodeSelections(labelsSelections || []) : undefined,
      defaultSelections: config.defaultLabelsSelections,
      openButtonVariant: pillVariant,
      onChange: onChange,
      onEditSelections: () => onEditSelections('labels')
    }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx(Headings, {
      nameSpace: nameSpace,
      widgetId: widgetId,
      sectionId: sectionId,
      hasHeading: config.hasHeading,
      title: config.title,
      openButtonVariant: pillVariant,
      onChange: updates => onChange('', updates)
    }), /*#__PURE__*/_jsx(Legend, _objectSpread({
      nameSpace: nameSpace,
      caption: `Add a legend showing each ${isCategorical ? 'category' : 'value'}`,
      hasLegend: Boolean(configWithOverrides.hasLegend),
      legendPosition: configWithOverrides.legendPosition,
      legends: configWithOverrides.legends,
      legendsCount: legendsCount,
      hasLegendInputs: true,
      openButtonVariant: pillVariant,
      onChange: updates => onChange('', updates)
    }, onEditSelections && {
      onEditSelections: () => onEditSelections('legends')
    })), /*#__PURE__*/_jsx(PointStyles, {
      nameSpace: nameSpace,
      openButtonVariant: pillVariant,
      variant: isPointStylesModified ? 'active' : '',
      pointRadius: (_configWithOverrides$ = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$ === void 0 ? void 0 : _configWithOverrides$.pointRadius,
      pointStyle: (_configWithOverrides$2 = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$2 === void 0 ? void 0 : _configWithOverrides$2.pointStyle,
      pointBackgroundColor: (_configWithOverrides$3 = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$3 === void 0 ? void 0 : _configWithOverrides$3.pointBackgroundColor,
      colorPallet: configWithOverrides.colorPallet,
      defaults: {
        pointRadius: defaultStyles.pointRadius,
        pointStyle: defaultStyles.pointStyle,
        pointBackgroundColor: defaultStyles.pointBackgroundColor
      },
      disabledPointStyles: DISABLED_POINT_STYLES,
      hasPointRadius: hasPointRadius,
      onChange: update => onChange('globalSeriesOptions', update)
    }, nameSpace), hasPointTemplate ? /*#__PURE__*/_jsx(Radius, {
      nameSpace: nameSpace,
      exampleValue: exampleValue,
      openButtonVariant: pillVariant,
      defaultTemplate: (_configWithOverrides$4 = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$4 === void 0 ? void 0 : _configWithOverrides$4.pointTemplate,
      pointTemplate: configWithOverrides === null || configWithOverrides === void 0 ? void 0 : configWithOverrides.pointTemplate,
      onChange: update => onChange('', update)
    }) : null, /*#__PURE__*/_jsx(AxisRevealPill, {
      nameSpace: `${nameSpace}-x-axes`,
      label: "X Axis",
      axisDisplay: configWithOverrides.xAxisDisplay,
      axisTitle: configWithOverrides.xAxisTitle,
      axisTemplate: configWithOverrides.xAxisTemplate,
      defaultTemplate: (_configWithOverrides$5 = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$5 === void 0 ? void 0 : _configWithOverrides$5.xAxisTemplate,
      exampleValue: exampleValue,
      openButtonVariant: pillVariant // xAxis scale
      ,
      onChange: updates => onChange('', deleteUndefinedValues({
        xAxisDisplay: updates.axisDisplay,
        xAxisTitle: updates.axisTitle,
        xAxisTemplate: updates.axisTemplate,
        xScaleMin: updates.scaleMin,
        xScaleMax: updates.scaleMax
      }))
    }), /*#__PURE__*/_jsx(AxisRevealPill, {
      nameSpace: `${nameSpace}-y-axes`,
      label: "Y Axis",
      axisDisplay: configWithOverrides.yAxisDisplay,
      axisTitle: configWithOverrides.yAxisTitle,
      axisTemplate: configWithOverrides.yAxisTemplate,
      defaultTemplate: (_configWithOverrides$6 = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$6 === void 0 ? void 0 : _configWithOverrides$6.yAxisTemplate,
      exampleValue: exampleValue,
      openButtonVariant: pillVariant,
      onChange: updates => onChange('', deleteUndefinedValues({
        yAxisDisplay: updates.axisDisplay,
        yAxisTitle: updates.axisTitle,
        yAxisTemplate: updates.axisTemplate,
        yScaleMin: updates.scaleMin,
        yScaleMax: updates.scaleMax
      }))
    }), /*#__PURE__*/_jsx(AxesGridLinesRevealPill, {
      nameSpace: nameSpace,
      colorPallet: colorPallet,
      openButtonVariant: pillVariant,
      axesGrid: {
        // xAxis
        xAxisGridVisible: configWithOverrides.xAxisGridVisible,
        xAxisGridColor: configWithOverrides.xAxisGridColor,
        xAxisGridBorderWidth: configWithOverrides.xAxisGridBorderWidth,
        xAxisGridBorderDash: configWithOverrides.xAxisGridBorderDash,
        // yAxis
        yAxisGridVisible: configWithOverrides.yAxisGridVisible,
        yAxisGridColor: configWithOverrides.yAxisGridColor,
        yAxisGridBorderWidth: configWithOverrides.yAxisGridBorderWidth,
        yAxisGridBorderDash: configWithOverrides.yAxisGridBorderDash
      },
      defaults: {
        xAxisGridVisible: defaultStyles.xAxisGridVisible,
        xAxisGridBorderWidth: defaultStyles.xAxisGridBorderWidth,
        xAxisGridColor: defaultStyles.xAxisGridColor,
        xAxisGridBorderDash: defaultStyles.xAxisGridBorderDash,
        yAxisGridVisible: defaultStyles.yAxisGridVisible,
        yAxisGridBorderWidth: defaultStyles.yAxisGridBorderWidth,
        yAxisGridColor: defaultStyles.yAxisGridColor,
        yAxisGridBorderDash: defaultStyles.yAxisGridBorderDash
      },
      onChange: updates => onChange('', updates)
    }), /*#__PURE__*/_jsx(ChartBoundaries, {
      nameSpace: nameSpace,
      openButtonVariant: pillVariant,
      forceZeroBaseline: (_configWithOverrides$7 = configWithOverrides.globalSeriesOptions) === null || _configWithOverrides$7 === void 0 ? void 0 : _configWithOverrides$7.forceZeroBaseline // xAxis scale
      ,
      xScaleMin: configWithOverrides.xScaleMin,
      xScaleDataMin: dataBoundaries.xMin,
      xScaleMinFramePercentage: configWithOverrides.xScaleMinFramePercentage,
      xScaleMax: configWithOverrides.xScaleMax,
      xScaleDataMax: dataBoundaries.xMax,
      xScaleMaxFramePercentage: configWithOverrides.xScaleMaxFramePercentage // yAxis scale
      ,
      yScaleMin: configWithOverrides.yScaleMin,
      yScaleDataMin: dataBoundaries.yMin,
      yScaleMinFramePercentage: configWithOverrides.yScaleMinFramePercentage,
      yScaleMax: configWithOverrides.yScaleMax,
      yScaleDataMax: dataBoundaries.yMax,
      yScaleMaxFramePercentage: configWithOverrides.yScaleMaxFramePercentage,
      onChange: updates => onChange('', updates)
    }), /*#__PURE__*/_jsx(DataLabels, {
      nameSpace: nameSpace,
      exampleValue: exampleValue,
      openButtonVariant: pillVariant,
      display: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$ = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$ === void 0 ? void 0 : _globalSeriesOptions$.display,
      anchoring: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$2 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$2 === void 0 ? void 0 : _globalSeriesOptions$2.anchoring,
      alignment: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$3 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$3 === void 0 ? void 0 : _globalSeriesOptions$3.alignment,
      rotation: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$4 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$4 === void 0 ? void 0 : _globalSeriesOptions$4.rotation,
      template: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$5 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$5 === void 0 ? void 0 : _globalSeriesOptions$5.template,
      color: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : (_globalSeriesOptions$6 = globalSeriesOptions.dataLabels) === null || _globalSeriesOptions$6 === void 0 ? void 0 : _globalSeriesOptions$6.color,
      colorPallet: colorPallet,
      defaults: {
        alignment: DEFAULT_DATA_LABEL_OPTIONS.align,
        anchoring: DEFAULT_DATA_LABEL_OPTIONS.anchor
      },
      onChange: update => onChange('globalSeriesOptions', {
        dataLabels: _objectSpread(_objectSpread({}, globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.dataLabels), update)
      })
    }), /*#__PURE__*/_jsx(TrendlineFields, {
      openButtonVariant: pillVariant,
      description: "Regression line corresponding to all data points.",
      isCreated: Boolean(configWithOverrides.trendline),
      nameSpace: nameSpace,
      display: ((_configWithOverrides$8 = configWithOverrides.trendline) === null || _configWithOverrides$8 === void 0 ? void 0 : _configWithOverrides$8.display) || false,
      borderWidth: (_configWithOverrides$9 = configWithOverrides.trendline) === null || _configWithOverrides$9 === void 0 ? void 0 : _configWithOverrides$9.borderWidth,
      borderDash: (_configWithOverrides$10 = configWithOverrides.trendline) === null || _configWithOverrides$10 === void 0 ? void 0 : _configWithOverrides$10.borderDash,
      borderColor: (_configWithOverrides$11 = configWithOverrides.trendline) === null || _configWithOverrides$11 === void 0 ? void 0 : _configWithOverrides$11.borderColor,
      backgroundColor: (_configWithOverrides$12 = configWithOverrides.trendline) === null || _configWithOverrides$12 === void 0 ? void 0 : _configWithOverrides$12.backgroundColor,
      tension: (_configWithOverrides$13 = configWithOverrides.trendline) === null || _configWithOverrides$13 === void 0 ? void 0 : _configWithOverrides$13.tension,
      hasLegend: Boolean(configWithOverrides.hasLegend),
      onChange: update => onChange('trendline', update),
      onDelete: () => onChange('', {
        trendline: null
      })
    }), /*#__PURE__*/_jsx(Animation, {
      nameSpace: nameSpace,
      animation: configWithOverrides.animation,
      openButtonVariant: pillVariant,
      onChange: update => onChange('animation', update)
    }), /*#__PURE__*/_jsx(Collapsible, {
      nameSpace: nameSpace,
      collapsible: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.collapsible,
      openButtonVariant: pillVariant,
      onChange: update => onChange('globalSeriesOptions', update)
    }), /*#__PURE__*/_jsx(ZeroBaselineToggle, {
      nameSpace: nameSpace,
      forceZeroBaseline: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.forceZeroBaseline,
      openButtonVariant: pillVariant,
      onChange: update => onChange('globalSeriesOptions', update)
    })]
  });
};

export default WidgetEditFormPointChartOptions;
export { defaultProps };